import React, { useState } from 'react';
import { useAssessment } from '../../context/AssessmentContext';
import ProgressPath from '../ui/ProgressPath';
import ParticleEffect from '../ui/ParticleEffect';
import AnimatedBackground from '../ui/AnimatedBackground';
import { CheckIcon, Flower2Icon, LeafIcon } from 'lucide-react';
const HealingHistory: React.FC = () => {
  const {
    currentStep,
    setCurrentStep,
    assessmentData,
    updateAssessmentData
  } = useAssessment();
  const [selectedOptions, setSelectedOptions] = useState<string[]>(assessmentData.healingHistory || []);
  const healingOptions = [{
    id: 'therapy',
    title: 'Traditional Therapy',
    description: 'Counseling, psychotherapy, or other talk therapy approaches'
  }, {
    id: 'meditation',
    title: 'Meditation & Mindfulness',
    description: 'Regular meditation, mindfulness practice, or breathwork'
  }, {
    id: 'yoga',
    title: 'Yoga & Movement',
    description: 'Yoga, tai chi, qigong, or other mindful movement practices'
  }, {
    id: 'energy',
    title: 'Energy Healing',
    description: 'Reiki, acupuncture, crystal healing, or other energy work'
  }, {
    id: 'plant',
    title: 'Plant Medicine',
    description: 'Herbal remedies, plant-based medicines, or tea ceremonies'
  }, {
    id: 'sound',
    title: 'Sound Healing',
    description: 'Sound baths, music therapy, or vibrational healing'
  }, {
    id: 'journaling',
    title: 'Journaling & Reflection',
    description: 'Regular writing, self-reflection, or shadow work'
  }, {
    id: 'community',
    title: 'Supportive Community',
    description: 'Support groups, healing circles, or spiritual communities'
  }, {
    id: 'nature',
    title: 'Nature Connection',
    description: 'Forest bathing, gardening, or other nature-based healing'
  }, {
    id: 'art',
    title: 'Creative Expression',
    description: 'Art therapy, dance, music, or other creative healing'
  }, {
    id: 'none',
    title: 'New to Healing',
    description: "I'm just beginning my healing journey"
  }];
  const toggleOption = (optionId: string) => {
    if (selectedOptions.includes(optionId)) {
      setSelectedOptions(selectedOptions.filter(id => id !== optionId));
    } else {
      setSelectedOptions([...selectedOptions, optionId]);
    }
  };
  const handleContinue = () => {
    updateAssessmentData({
      healingHistory: selectedOptions
    });
    setCurrentStep(currentStep + 1);
  };
  return <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative" data-id="element-600">
      <AnimatedBackground type="cosmic" data-id="element-601" />
      <ParticleEffect count={50} data-id="element-602" />
      {/* Decorative elements */}
      <div className="absolute top-10 left-10 garden-leaf opacity-20" data-id="element-603">
        <LeafIcon size={80} className="text-green-200" data-id="element-604" />
      </div>
      <div className="absolute bottom-20 right-20 garden-leaf opacity-20" data-id="element-605">
        <LeafIcon size={100} className="text-green-200" data-id="element-606" />
      </div>
      <div className="absolute top-1/4 right-1/4 garden-flower opacity-20" data-id="element-607">
        <Flower2Icon size={60} className="text-pink-200" data-id="element-608" />
      </div>
      <div className="w-full max-w-4xl" data-id="element-609">
        <h1 className="text-4xl md:text-5xl font-bold text-center text-white mb-2" data-id="element-610">
          <span className="text-pink-300" data-id="element-611">Seven</span>After
        </h1>
        <ProgressPath totalSteps={7} currentStep={2} data-id="element-612" />
        <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white border-opacity-20 shadow-xl relative overflow-hidden" data-id="element-613">
          <div className="spiral-pattern absolute inset-0 opacity-20 z-0" data-id="element-614"></div>
          <div className="lotus-pattern absolute inset-0 opacity-20 z-0" data-id="element-615"></div>
          <h2 className="text-2xl md:text-3xl font-bold text-center text-white mb-4 relative z-10" data-id="element-616">
            Your Healing Journey So Far
          </h2>
          <p className="text-white text-center mb-6 relative z-10" data-id="element-617">
            Select any healing modalities you've explored in your journey. This
            helps us personalize your experience.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 relative z-10" data-id="element-618">
            {healingOptions.map(option => <button key={option.id} onClick={() => toggleOption(option.id)} className={`
                  flex items-start p-4 rounded-xl text-left transition-all
                  ${selectedOptions.includes(option.id) ? 'bg-pink-500 bg-opacity-30 border border-pink-400 border-opacity-60' : 'bg-white bg-opacity-10 border border-white border-opacity-20 hover:bg-opacity-15'}
                `} data-id="element-619">
                <div className={`
                  w-6 h-6 rounded-full mr-3 flex-shrink-0 flex items-center justify-center mt-0.5
                  ${selectedOptions.includes(option.id) ? 'bg-pink-500' : 'bg-white bg-opacity-20'}
                `} data-id="element-620">
                  {selectedOptions.includes(option.id) && <CheckIcon size={14} className="text-white" data-id="element-621" />}
                </div>
                <div data-id="element-622">
                  <h3 className="font-medium text-white" data-id="element-623">{option.title}</h3>
                  <p className="text-white text-opacity-80 text-sm mt-1" data-id="element-624">
                    {option.description}
                  </p>
                </div>
              </button>)}
          </div>
          <button onClick={handleContinue} className="w-full py-3 rounded-xl transition-all duration-300 text-white font-semibold bg-gradient-to-r from-pink-500 to-orange-500 hover:shadow-lg cursor-pointer relative z-10" data-id="element-625">
            Continue Your Journey
          </button>
        </div>
      </div>
    </div>;
};
export default HealingHistory;